import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Copy, RefreshCw, ArrowRightLeft } from 'lucide-react';
import { toast } from 'sonner';

const BytesConverterTool = () => {
  const [value, setValue] = useState('1024');
  const [unit, setUnit] = useState('KB');
  const [useBinary, setUseBinary] = useState(true);

  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const base = useBinary ? 1024 : 1000;

  const convertToBytes = (val, fromUnit) => {
    const index = units.indexOf(fromUnit);
    return parseFloat(val) * Math.pow(base, index);
  };

  const convertFromBytes = (bytes, toUnit) => {
    const index = units.indexOf(toUnit);
    const result = bytes / Math.pow(base, index);
    return result < 0.01 ? result.toExponential(2) : result.toFixed(2).replace(/\.?0+$/, '');
  };

  const bytes = convertToBytes(value, unit);

  const copyValue = (val, u) => {
    navigator.clipboard.writeText(`${val} ${u}`);
    toast.success('Copied!');
  };

  return (
    <div className="space-y-6" data-testid="bytes-converter-tool">
      <div className="flex gap-4 items-center">
        <label className="flex items-center gap-2">
          <input
            type="radio"
            checked={useBinary}
            onChange={() => setUseBinary(true)}
            className="rounded"
          />
          <span className="text-sm">Binary (1024)</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="radio"
            checked={!useBinary}
            onChange={() => setUseBinary(false)}
            className="rounded"
          />
          <span className="text-sm">Decimal (1000)</span>
        </label>
      </div>

      <div className="flex gap-2 items-end">
        <div className="flex-1">
          <label className="text-sm text-muted-foreground mb-2 block">Value</label>
          <Input
            type="number"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="font-mono"
          />
        </div>
        <div className="w-24">
          <label className="text-sm text-muted-foreground mb-2 block">Unit</label>
          <select
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            className="w-full h-10 px-3 rounded-md bg-muted border border-border font-mono"
          >
            {units.map(u => (
              <option key={u} value={u}>{u}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="p-4 rounded-xl bg-muted/30 border border-border">
        <h3 className="font-semibold mb-4">Conversions</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {units.map(u => {
            const converted = convertFromBytes(bytes, u);
            return (
              <div 
                key={u} 
                className="p-3 rounded-lg bg-background border border-border flex justify-between items-center"
              >
                <div>
                  <span className="text-muted-foreground text-sm">{u}</span>
                  <p className="font-mono">{converted}</p>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8"
                  onClick={() => copyValue(converted, u)}
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="p-4 rounded-xl bg-primary/10 border border-primary/20">
        <h3 className="font-semibold mb-2">Reference</h3>
        <div className="text-sm text-muted-foreground space-y-1">
          <p>Binary (computer storage): 1 KB = 1024 bytes</p>
          <p>Decimal (marketing): 1 KB = 1000 bytes</p>
          <p className="text-xs mt-2 opacity-70">
            Note: Hard drive manufacturers often use decimal, while operating systems use binary.
          </p>
        </div>
      </div>

      <div className="flex gap-2 justify-center">
        <Button onClick={() => { setValue('1024'); setUnit('KB'); }} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" /> Reset
        </Button>
      </div>
    </div>
  );
};

export default BytesConverterTool;
